import Alpine from 'alpinejs'
import persist from '@alpinejs/persist'
import Leaflet from 'leaflet'
import countryGeoData from './geo'
import LAGeoData from './LAs.json'

import './heatDemandScenarioStock'
import './heatDemandScenarioWeather'
import './heatDemandScenarioOutputs'
import './flexibilityQuantificationScenario'

document.addEventListener('alpine:init', () => {
  Alpine.plugin(persist)
  Alpine.store('scenarioState', {
    selectedSpatialResolution: Alpine.$persist('GB').as('selectedSpatialResolution'),
    selectedYear: Alpine.$persist('2025').as('selectedYear'),
    selectedCountry: Alpine.$persist('Wales').as('selectedCountry'),
    selectedLA: Alpine.$persist('Cardiff').as('selectedLA')
  })

  Alpine.bind('chartDownload', () => ({
    type: 'button',

    '@click' () {
      const chart = window[this.$el.dataset.charttarget]
      // First create a second canvas in order to give the eventual image a white background
      const canvas2 = document.createElement('canvas')
      canvas2.width = chart.width
      canvas2.height = chart.height
      const ctx = canvas2.getContext('2d')
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas2.width, canvas2.height)
      ctx.drawImage(chart, 0, 0)
      const url = canvas2.toDataURL()

      // Now download the URL
      const element = document.createElement('a')
      element.setAttribute('href', url)
      element.setAttribute('download', 'image.png')
      element.style.display = 'none'
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    }
  }))

  Alpine.bind('chartDownloadData', () => ({
    type: 'button',

    '@click' () {
      const { labels, datasets } = JSON.parse(window[this.$el.dataset.datatarget].value)
      let csv = 'Labels,' + datasets.map(ds => `${ds.label}`.replaceAll(',', ' ')).join(',') + '\n'
      // The below code handles instances where each value in the dataset data is an object (e.g. for a boxplot)
      // However, in future this could be handled more nicely.
      labels.forEach((label, i) => {
        csv += `${label}`.replaceAll(',', ' ') + ',' + datasets.map(ds => {
          if (typeof ds.data[i] === 'object') {
            // Handle cases where each data point is a coordinate
            if (ds.data[i].y !== undefined) {
              return ds.data[i].y
            }
            // Otherwise generically handle object by stringifying
            return JSON.stringify(ds.data[i]).replaceAll(',', ' ')
          } else {
            // Default case - just return the data value
            return ds.data[i]
          }
        }).join(',') + '\n'
      })
      const dataUrl = `data:text/csv;base64,${btoa(csv)}`

      // Download the data URL
      const element = document.createElement('a')
      element.setAttribute('href', dataUrl)
      element.setAttribute('download', 'data.csv')
      element.style.display = 'none'
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    }
  }))

  Alpine.data('map', () => {
    let map
    let gbPolygon = null
    let countryPolygons = null
    let laPolygons = null
    const defaultCoords = [54.5, -3]
    const defaultPolygonStyle = {
      fillColor: 'gray',
      fillOpacity: 0.5,
      color: 'white',
      weight: 2
    }
    const selectedPolygonStyle = {
      fillColor: 'blue',
      fillOpacity: 0.5,
      color: 'white',
      weight: 2
    }
    return {
      init () {
        setTimeout(this.doBindings, 1000)
      },
      doBindings () {
        // Initialise the map
        map = Leaflet.map('map', {
          zoomControl: false
        }).setView(defaultCoords, 5)
        map.attributionControl.setPrefix('')
        Leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map)

        // If click on the non-polygon area, reset the selection
        map.on('click', () => {
          Alpine.store('scenarioState').selectedSpatialResolution = 'GB'
        })

        // Handle changes to selected spatial resolution
        Alpine.effect(() => {
          const spatialResolution = Alpine.store('scenarioState').selectedSpatialResolution
          // Remove all current polygons
          if (gbPolygon) { map.removeLayer(gbPolygon); gbPolygon = null }
          if (laPolygons) map.removeLayer(laPolygons)
          if (countryPolygons) {
            Object.values(countryPolygons).forEach(polygon => map.removeLayer(polygon))
          }
          if (spatialResolution === 'GB') {
            gbPolygon = Leaflet.geoJSON(countryGeoData.GB, { style: selectedPolygonStyle }).addTo(map)
            map.flyTo(defaultCoords, 5)
          } else if (spatialResolution === 'Country') {
            if (laPolygons) map.removeLayer(laPolygons)
            countryPolygons = {}
            Object.keys(countryGeoData).filter(key => key !== 'GB').forEach(key => {
              countryPolygons[key] = Leaflet.geoJSON(countryGeoData[key], { style: defaultPolygonStyle })
              countryPolygons[key].bindTooltip(key)
              countryPolygons[key].addTo(map)
              countryPolygons[key].on('click', e => {
                Alpine.store('scenarioState').selectedCountry = key
                e.originalEvent.view.L.DomEvent.stopPropagation(e) // Prevent GB selection. Need to access Leaflet event object
              })
            })
          } else if (spatialResolution === 'LA') {
            laPolygons = Leaflet.geoJSON(LAGeoData, {
              style: defaultPolygonStyle,
              onEachFeature: (feature, layer) => {
                layer.bindTooltip(feature.properties.LAD24NM)
                layer.on('click', e => {
                  Alpine.store('scenarioState').selectedLA = feature.properties.LAD24NM
                  e.originalEvent.view.L.DomEvent.stopPropagation(e)
                })
              }
            })
            laPolygons.addTo(map)
          }
        })

        // Handle changes to the selected Local Authority (update map)
        Alpine.effect(() => {
          const spatialResolution = Alpine.store('scenarioState').selectedSpatialResolution
          if (spatialResolution !== 'LA') return
          const la = Alpine.store('scenarioState').selectedLA
          laPolygons?.eachLayer(layer => {
            if (layer.feature.properties.LAD24NM === la) {
              layer.setStyle(selectedPolygonStyle)
              const coords = [layer.feature.properties?.LAT, layer.feature.properties?.LONG]
              if (coords) map.flyTo(coords, 7, { duration: 1 })
            } else {
              layer.setStyle(defaultPolygonStyle)
            }
          })
        })

        // Handle changes to the selected country (update map)
        Alpine.effect(() => {
          const spatialResolution = Alpine.store('scenarioState').selectedSpatialResolution
          if (spatialResolution !== 'Country') return
          const country = Alpine.store('scenarioState').selectedCountry
          if (countryPolygons && countryPolygons[country]) {
            const coords = countryGeoData[country].properties?.centre
            if (coords) map.flyTo(coords, 6, { duration: 1 })
            Object.values(countryPolygons).forEach(polygon => polygon.setStyle(defaultPolygonStyle))
            countryPolygons[Alpine.store('scenarioState').selectedCountry].setStyle(selectedPolygonStyle)
          }
        })
      }
    }
  })
})
